/**
 * card-variant-picker.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Matches the PDP variant-picker logic exactly, combined with the Alt text 
 * image filtering for product cards.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  const FILTERABLE_OPTIONS = ['buckle color', 'buckle style', 'by occasion'];

  class CardVariantPicker extends HTMLElement {
    constructor() {
      super();
      this.variants = [];
      this.selectedOptions = [];
      this.handleChange = this.handleChange.bind(this);
    }

    connectedCallback() {
      const variantsScript = this.querySelector(".variants-data");
      if (!variantsScript?.textContent.trim()) return;

      try {
        this.variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error("Invalid JSON in .variants-data", e);
        return;
      }

      this.querySelectorAll("input").forEach((input) =>
        input.addEventListener("change", this.handleChange)
      );

      this.querySelectorAll("label").forEach((label) =>
        label.addEventListener("pointerover", (e) => e.stopPropagation())
      );

      // Run on initial load to set correct disabled states
      requestAnimationFrame(() => {
        this.handleChange();
      });
    }

    handleChange(event) {
      if (event) {
        event.stopPropagation();
      }

      // User's exact requested logic for gathering selected options
      this.selectedOptions = Array.from(
        this.querySelectorAll("input:checked"),
        (input) => input.value
      );

      // Update active label text if user clicked a radio
      if (event && event.target && event.target.type === 'radio') {
        const fieldset = event.target.closest('fieldset');
        const label = fieldset?.querySelector('.selected-value-label');
        if (label) label.textContent = event.target.value;
      }

      this.updateInputAvailability();

      // Update variant URLs to replace theme.js functionality
      const productCard = this.closest('product-card');
      const matchingVariant = this.variants.find((variant) =>
        variant.options.every((opt, i) => this.selectedOptions[i] === opt)
      );

      if (productCard && matchingVariant) {
        const handle = productCard.getAttribute('handle');
        if (handle) {
          productCard.querySelectorAll(`a[href*="/products/${handle}"]`).forEach((link) => {
            try {
              const url = new URL(link.href, window.location.origin);
              url.searchParams.set("variant", matchingVariant.id);
              link.href = `${url.pathname}${url.search}${url.hash}`;
            } catch (e) { }
          });
        }
      }

      this.applyAltFilterLogic();
    }

    updateInputAvailability() {
      const hasCombination = (variant, level, value) => {
        return Array.from({ length: level + 1 }, (_, i) => {
          if (i === level) {
            return variant.options[level] === value;
          } else {
            return variant.options[i] === this.selectedOptions[i];
          }
        }).every((condition) => condition);
      };

      this.querySelectorAll("fieldset").forEach((group, optionIndex) => {
        let firstVisibleEnabledRadio = null;
        let selectedRadioWasDisabled = false;

        group.querySelectorAll("input").forEach((input) => {
          const value = input.value;

          const hasAtLeastOneCombination = this.variants.some(
            (variant) => hasCombination(variant, optionIndex, value) && variant
          );
          const hasAvailableVariant = this.variants.some(
            (variant) => hasCombination(variant, optionIndex, value) && variant.available
          );

          // Add disabled class/state
          input.classList.toggle("is-disabled", !hasAvailableVariant);
          input.classList.toggle("disabled", !hasAvailableVariant);
          input.disabled = !hasAvailableVariant;

          // Toggle 'hidden' attribute just like theme.js for combinations that don't exist
          const label = input.nextElementSibling;
          if (label && label.tagName === 'LABEL') {
            label.toggleAttribute("hidden", !hasAtLeastOneCombination);
          }
          input.toggleAttribute("hidden", !hasAtLeastOneCombination);

          if (hasAvailableVariant && !firstVisibleEnabledRadio) {
            firstVisibleEnabledRadio = input;
          }

          if (input.checked && (!hasAtLeastOneCombination || !hasAvailableVariant)) {
            selectedRadioWasDisabled = true;
          }
        });

        // Auto-select next available if current is now invalid
        if (selectedRadioWasDisabled && firstVisibleEnabledRadio) {
          firstVisibleEnabledRadio.checked = true;
          this.selectedOptions[optionIndex] = firstVisibleEnabledRadio.value;
          const labelText = group.querySelector('.selected-value-label');
          if (labelText) labelText.textContent = firstVisibleEnabledRadio.value;
          selectedRadioWasDisabled = false;
        }
      });
    }

    /* ─── Alt-Text Image Logic ────────────────────────────────────────────── */

    applyAltFilterLogic() {
      const productCard = this.closest('product-card');
      if (!productCard) return;

      const filterVals = {};
      let hasFilterableOption = false;

      this.querySelectorAll('fieldset').forEach((fieldset) => {
        const optName = (fieldset.getAttribute('data-option-name') || '').toLowerCase().trim();
        if (FILTERABLE_OPTIONS.includes(optName)) {
          const checked = fieldset.querySelector('input:checked');
          if (checked) {
            filterVals[optName] = checked.value.toLowerCase().replace(/(\w)-(\w)/g, '$1 $2').trim();
            hasFilterableOption = true;
          }
        }
      });

      if (!hasFilterableOption) return;

      const spans = productCard.querySelectorAll('alt-logic-swatch span[data-alt][data-img]');
      const matchedImages = [];
      const filteredMediaIds = [];

      spans.forEach((span, index) => {
        const alt = (span.getAttribute('data-alt') || '').trim();
        const altLower = alt.toLowerCase().replace(/(\w)-(\w)/g, '$1 $2');
        const imgUrl = span.getAttribute('data-img') || '';
        const mediaId = span.getAttribute('data-media-id') ? parseInt(span.getAttribute('data-media-id')) : null;

        if (alt === '' || alt.includes('|') || alt.includes('*') || altLower.includes('global') || altLower.includes('common') || altLower.includes('always')) {
          return;
        }

        const altParts = altLower.split(',').map(part => part.trim());
        let isAnyPartMatch = false;

        for (const part of altParts) {
          let isMatch = true;
          for (const [optName, optValue] of Object.entries(filterVals)) {
            if (!part.includes(optValue)) {
              isMatch = false;
              break;
            }
          }
          if (isMatch) {
            isAnyPartMatch = true;
            break;
          }
        }

        if (!isAnyPartMatch) {
          filteredMediaIds.push(index);
        } else {
          matchedImages.push({ alt: alt, url: imgUrl, index: index, mediaId: mediaId });
        }
      });

      const matchingVariant = this.variants.find((variant) =>
        variant.options.every((opt, i) => this.selectedOptions[i] === opt)
      );

      let updatedImage = null;
      let targetMediaId = null;

      const featuredObj = matchingVariant ? (matchingVariant.featured_media || matchingVariant.featured_image) : null;

      if (featuredObj) {
        const featuredUrl = featuredObj.src || (featuredObj.preview_image && featuredObj.preview_image.src) || '';

        // If the featured media is in the filtered list (i.e. it's hidden by alt logic), use the first matched valid image instead
        if (filteredMediaIds.includes(featuredObj.position - 1)) {
          if (matchedImages.length > 0) {
            updatedImage = matchedImages[0].url;
          }
        } else {
          updatedImage = featuredUrl;
        }
      } else if (matchedImages.length > 0) {
        updatedImage = matchedImages[0].url;
      }

      if (updatedImage) {
        this.updateCardImage(productCard, updatedImage);
      }

      productCard.dispatchEvent(
        new CustomEvent('card:media:filtered', {
          bubbles: true,
          detail: { filteredMediaIds, matchedImages, filterVals: filterVals },
        })
      );
    }

    updateCardImage(productCard, imgUrl) {
      const img = productCard.querySelector('.product-card__media img');
      if (img && imgUrl) {
        img.src = imgUrl;
        img.srcset = imgUrl;
      }
    }
  }

  if (!window.customElements.get('card-variant-picker')) {
    window.customElements.define('card-variant-picker', CardVariantPicker);
  }
})();
